import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FiTruck, FiAlertTriangle, FiCreditCard } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TXN_LABEL = {
  delivery_charge: 'Delivery charge',
  cash_topup: 'Cash top-up',
  upi_topup: 'UPI top-up',
  admin_topup: 'Admin top-up',
  razorpay_topup: 'Online top-up',
  admin_adjustment: 'Adjustment',
};

const MIN_TOPUP = 50;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function DeliveryBalanceCard({ user, getAuthHeader, onBalanceChange, variant = 'card' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/me/delivery-balance`, { headers: getAuthHeader() });
      setData(res.data);
      onBalanceChange?.(res.data.balance);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, onBalanceChange]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleTopup = async (overrideAmount) => {
    const amt = parseFloat(overrideAmount ?? amount);
    if (!amt || amt < MIN_TOPUP) {
      setError(`Minimum top-up is ₹${MIN_TOPUP}`);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError('Failed to load payment library');
        setSubmitting(false);
        return;
      }
      const orderRes = await axios.post(
        `${API}/delivery/topup/create-order`,
        { amount: amt },
        { headers: getAuthHeader() }
      );
      const opts = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        order_id: orderRes.data.order_id,
        name: 'The Hyderabad Library',
        description: 'Delivery balance top-up',
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#7c3aed' },
        handler: async (resp) => {
          try {
            const verifyRes = await axios.post(
              `${API}/delivery/topup/verify-payment`,
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { headers: getAuthHeader() }
            );
            alert(verifyRes.data.message || 'Top-up successful');
            setAmount('');
            fetch();
          } catch (verr) {
            setError(verr.response?.data?.detail || 'Verification failed');
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on('payment.failed', () => {
        setError('Payment failed');
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start top-up');
      setSubmitting(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-gray-500" data-testid="delivery-card-loading">
        Loading delivery balance…
      </div>
    );
  }

  const owes = data.owes_money;
  const owed = data.amount_owed;
  const balance = data.balance;

  // Compact banner variant for cart/catalog pages
  if (variant === 'banner') {
    if (!owes) return null;
    return (
      <div
        className="bg-red-50 border border-red-300 text-red-800 rounded-xl px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
        data-testid="delivery-blocked-banner"
      >
        <FiAlertTriangle className="text-red-600 shrink-0" size={20} />
        <div className="flex-1 text-sm">
          <div className="font-semibold">
            You owe ₹{owed.toFixed(2)} in delivery fees.
          </div>
          <div>Please clear your delivery balance to place new holds or orders.</div>
        </div>
        <button
          onClick={() => handleTopup(Math.max(owed, MIN_TOPUP))}
          disabled={submitting}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg text-sm whitespace-nowrap disabled:opacity-50"
          data-testid="banner-pay-now-btn"
        >
          {submitting ? 'Opening…' : `Pay ₹${Math.max(owed, MIN_TOPUP).toFixed(0)} now`}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="delivery-balance-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FiTruck className="text-purple-600" /> Delivery Balance
        </h2>
      </div>

      <div
        className={`rounded-lg px-4 py-3 mb-4 ${
          owes ? 'bg-red-50 border border-red-200' : 'bg-purple-50 border border-purple-200'
        }`}
      >
        <div className="text-xs text-gray-600">Current balance</div>
        <div
          className={`text-3xl font-bold ${owes ? 'text-red-700' : 'text-purple-700'}`}
          data-testid="my-delivery-balance"
        >
          ₹{balance.toFixed(2)}
        </div>
        {owes ? (
          <div className="text-xs text-red-700 mt-1 flex items-center gap-1">
            <FiAlertTriangle /> You owe ₹{owed.toFixed(2)} — clear to place new holds.
          </div>
        ) : (
          <div className="text-xs text-gray-600 mt-1">
            Prepay so future deliveries auto-deduct from this balance.
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <label className="block text-xs text-gray-600 mb-1">Top-up amount (min ₹{MIN_TOPUP})</label>
        <div className="flex gap-2">
          <input
            type="number"
            min={MIN_TOPUP}
            step="50"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={owes ? String(Math.max(owed, MIN_TOPUP).toFixed(0)) : '200'}
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
            data-testid="topup-amount-input"
          />
          <button
            onClick={() => handleTopup()}
            disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
            data-testid="topup-submit-btn"
          >
            <FiCreditCard /> {submitting ? '…' : 'Pay'}
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          {[100, 200, 500].map((v) => (
            <button
              key={v}
              onClick={() => setAmount(String(v))}
              className="text-xs px-3 py-1 rounded-full bg-white border hover:bg-purple-50"
              data-testid={`quick-topup-${v}`}
            >
              ₹{v}
            </button>
          ))}
        </div>
        {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
      </div>

      <div>
        <div className="text-sm font-semibold text-gray-800 mb-2">Recent activity</div>
        {(data.transactions || []).length === 0 ? (
          <div className="text-sm text-gray-500">No transactions yet.</div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.transactions.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between text-xs border-b last:border-b-0 py-1.5"
              >
                <div>
                  <div className="font-medium text-gray-800">
                    {TXN_LABEL[t.type] || t.type}
                  </div>
                  <div className="text-gray-500">
                    {new Date(t.created_at).toLocaleString()}
                  </div>
                </div>
                <div className={`font-semibold ${t.delta < 0 ? 'text-red-600' : 'text-green-700'}`}>
                  {t.delta < 0 ? '-' : '+'}₹{Math.abs(t.delta).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
